/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { Else, If, Then } from "react-if";
import {
  FlexContainer,
  theme,
  Box,
  Spacer,
  InputSearch,
} from "@nordcloud/gnui";
import { SortOrder } from "~/generated/graphql";
import { NoData, SortSelector } from "~/components";
import { TableWrapper } from "~/components/Tables";
import { isEmpty } from "~/tools";
import { updateReportColumnLabels } from "../const";
import {
  type UpdateReportOverviewData,
  type UpdateReportTableState,
  type UpdateReportDetail,
  type UpdateReportSortField,
  UpdateReportItemStatus,
  type ActionWithMissingData,
} from "../types";
import { GenerateUpdatesReport } from "./GenerateUpdatesReport";
import { MissingLogsMessage } from "./MissingLogsMessage";
import { TableFilterPopover } from "./TableFilterPopover";
import { updatesTableColumns } from "./UpdatesTableColumns";

const TABLE_AREA_MIN_HEIGHT = "36vh";

const SORT_FIELDS: UpdateReportSortField[] = [
  "name",
  "time",
  "patchPolicy",
  "rebootAllowed",
  "releaseDate",
  "category",
  "status",
];

const SORT_OPTIONS = SORT_FIELDS.map((value) => ({
  label: updateReportColumnLabels[value],
  value,
}));

type Props = {
  data: UpdateReportOverviewData | null;
  loading: boolean;
  tableState: UpdateReportTableState;
  updates: UpdateReportDetail[];
  resourceId: string;
  resource?: {
    id: string;
    name?: string | null;
    os?: string | null;
  };
  reportPeriod: {
    startDate: string;
    endDate: string;
  };
  hasUpdates: boolean;
  missingLogSections: {
    key: string;
    title: string;
    actionsWithMissingData: ActionWithMissingData[];
  }[];
  onSearch: (value?: string) => void;
  onSortField: (field: UpdateReportSortField) => void;
  onSortDirection: (dir: SortOrder) => void;
  selectedStatuses: UpdateReportItemStatus[];
  onChangeStatus: (statuses: UpdateReportItemStatus[]) => void;
};

export function UpdatesTable({
  data,
  loading,
  tableState,
  updates,
  resourceId,
  resource,
  reportPeriod,
  hasUpdates,
  missingLogSections,
  onSearch,
  onSortDirection,
  onSortField,
  selectedStatuses,
  onChangeStatus,
}: Props) {
  const tableColumns = useMemo(() => updatesTableColumns, []);
  const [searchInput, setSearchInput] = useState<string>(
    tableState.search ?? ""
  );
  useEffect(() => {
    setSearchInput(tableState.search ?? "");
  }, [tableState.search]);

  const applySearch = (value: string) => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      onSearch(undefined);
      return;
    }

    onSearch(trimmed);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    const trimmed = value.trim();

    if (trimmed.length === 0) {
      onSearch(undefined);
      return;
    }

    applySearch(value);
  };

  const handleSearchSubmit = (value: string) => {
    applySearch(value);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(event.target.value);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      handleSearchSubmit((event.target as HTMLInputElement).value);
    }
  };

  return (
    <div css={{ width: "100%" }}>
      <Box boxStyle="lightGrey">
        <FlexContainer justifyContent="space-between">
          <SortSelector<UpdateReportSortField>
            options={SORT_OPTIONS}
            value={tableState.sortField}
            direction={tableState.sortDirection}
            onSelect={onSortField}
            onSortChange={onSortDirection}
          />
          <Spacer width={theme.spacing.spacing04} />
          <div css={{ width: "100%" }}>
            <InputSearch
              value={searchInput}
              onChange={handleInputChange}
              onSearch={() => handleSearchSubmit(searchInput)}
              onKeyDown={handleInputKeyDown}
              placeholder="Type a Keyword"
            />
          </div>

          <Spacer width={theme.spacing.spacing04} />
          <TableFilterPopover
            details={data}
            selectedStatuses={selectedStatuses}
            onChangeStatus={onChangeStatus}
            hasUpdates={hasUpdates}
          />
          <Spacer width={theme.spacing.spacing04} />
          <GenerateUpdatesReport
            resourceId={resourceId}
            resource={resource}
            reportPeriod={reportPeriod}
            hasUpdates={hasUpdates}
          />
        </FlexContainer>
      </Box>

      <If condition={missingLogSections.length > 0}>
        <Then>
          <FlexContainer
            direction="column"
            gap={theme.spacing.spacing04}
            css={{
              width: "100%",
              marginTop: theme.spacing.spacing04,
              marginBottom: theme.spacing.spacing04,
            }}
          >
            {missingLogSections.map((section) => (
              <MissingLogsMessage
                key={section.key}
                resourceId={resourceId}
                actionsWithMissingData={section.actionsWithMissingData}
                title={section.title}
              />
            ))}
          </FlexContainer>
        </Then>
      </If>

      <div
        css={{
          minHeight: TABLE_AREA_MIN_HEIGHT,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <If condition={isEmpty(updates)}>
          <Then>
            <FlexContainer
              alignItems="center"
              justifyContent="center"
              css={{ height: "100%", width: "100%" }}
            >
              <NoData hasIcon message={"There are no Updates."} />
            </FlexContainer>
          </Then>
          <Else>
            <div css={{ flex: 1 }}>
              <TableWrapper<UpdateReportDetail>
                data={updates}
                columns={tableColumns}
                loading={loading}
                stripped
                rowSelection={{}}
                setRowSelection={() => {}}
              />
            </div>
          </Else>
        </If>
      </div>
    </div>
  );
}
